import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap, Trash2
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, HistorialPedidoCard } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF, guardarPDF, agruparPorMaterial } from "./helpers";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters, sortPendingFirst } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";

export function PtosFinalesTab({
  finalBudgets, budgetActs, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [showNew, setShowNew] = useState(false);
  const [selectedAPId, setSelectedAPId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [viewPF, setViewPF] = useState(null);
  const [editPF, setEditPF] = useState(null);
  const [editData, setEditData] = useState({ sucursal: "", condicion_pago: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const pfFileInputRef = useRef(null);
  const [uploadingPFId, setUploadingPFId] = useState(null);
  const [provSucursales, setProvSucursales] = useState([]);  // branches from catalog
  const [provCondiciones, setProvCondiciones] = useState([]); // condiciones de pago from catalog
  const [aiPF, setAiPF] = useState(null);            // PF being AI-analyzed
  const [aiFile, setAiFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");

  const handleDeletePF = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/final-budgets/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`PF ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar PF"); }
    finally { setDeleting(false); }
  };

  const handleRevertToAnalysis = async (pf) => {
    // BLOQUE 22 PARTE B — "retroceder el presupuesto final y volver a analizar":
    // reutiliza el mismo endpoint reopen del AP de origen (soft-deletea el/los PF sin
    // OC y devuelve el AP a "abierta"). El backend bloquea si ya hay OC generada.
    if (!pf.ap_id) { toast.error("Este PF no tiene un Análisis de Presupuesto de origen"); return; }
    if (!window.confirm(`¿Revertir el PF ${pf.number} y volver a analizar el AP ${pf.ap_number || ""}? El/los Presupuesto(s) Final(es) sin Orden de Compra serán revertidos.`)) return;
    setRevertingId(pf._id);
    try {
      await axios.put(`${API}/purchases/budget-acts/${pf.ap_id}/reopen`, {}, { withCredentials: true });
      toast.success(`${pf.ap_number || "AP"} reabierto para volver a analizar`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al revertir a análisis"); }
    finally { setRevertingId(null); }
  };

  const handleAIAnalyze = async () => {
    if (!aiPF || !aiFile) { toast.error("Adjunte el documento del proveedor"); return; }
    if (aiFile.size > 5 * 1024 * 1024) { toast.error("Archivo > 5MB"); return; }
    setAiLoading(true);
    setAiResult(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const r = await axios.post(`${API}/purchases/final-budgets/${aiPF._id}/ai-analyze`, {
          file_data: ev.target.result,
          file_name: aiFile.name,
          file_type: aiFile.type,
        }, { withCredentials: true });
        setAiResult(r.data);
        toast.success("Validación IA completada");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error en análisis IA");
      } finally {
        setAiLoading(false);
      }
    };
    reader.readAsDataURL(aiFile);
  };

  const canEdit = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);

  const handleUploadPFDoc = (pfId) => {
    setUploadingPFId(pfId);
    pfFileInputRef.current.click();
  };

  const handlePFFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingPFId) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("El archivo supera 3MB. Comprímaló antes de subir.");
      e.target.value = "";
      setUploadingPFId(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await axios.put(`${API}/purchases/final-budgets/${uploadingPFId}/upload-doc`, {
          file_data: ev.target.result,
          file_name: file.name,
          file_type: file.type,
        }, { withCredentials: true });
        toast.success("Presupuesto aprobado adjuntado correctamente");
        onRefresh();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al subir documento");
      } finally {
        setUploadingPFId(null);
        e.target.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  // APs cerrados que todavía no tienen PFs generados y tienen al menos 1 proveedor
  const closedAPsForPF = budgetActs.filter(act =>
    act.status === "cerrada" && !act.pf_generated && !act.cubierto_por_stock && (act.provider_columns || []).length > 0
  );
  const baseFilteredPFs = finalBudgets.filter(pf => matchClienteObraPresupuesto(pf, { filterCliente, filterObra, filterPresupuesto }, { listasClientes, listasObras }));
  const pfStatusCounts = baseFilteredPFs.reduce((acc, pf) => { acc[pf.status] = (acc[pf.status] || 0) + 1; return acc; }, {});
  const filteredPFsUnsorted = baseFilteredPFs.filter(pf => {
    if (filterStatus !== "todos" && pf.status !== filterStatus) return false;
    if (filterUrgency && pf.urgency !== filterUrgency) return false;
    if (search) {
      const s = search.toLowerCase();
      const hit = (pf.number || "").toLowerCase().includes(s) || (pf.project_name || "").toLowerCase().includes(s) ||
        (pf.ap_number || "").toLowerCase().includes(s) || (pf.provider_name || "").toLowerCase().includes(s) ||
        (pf.items || []).some(i => (i.item_name || "").toLowerCase().includes(s));
      if (!hit) return false;
    }
    return true;
  });
  // BLOQUE 50 PARTE B — PF sin OC generada arriba, con OC/facturados abajo; más nuevo primero.
  const filteredPFs = sortPendingFirst(filteredPFsUnsorted, pf => pf.status === "borrador");

  const handleGenerate = async (apId) => {
    const id = apId || selectedAPId;
    if (!id) { toast.error("Seleccione un AP"); return; }
    setGenerating(true);
    try {
      await axios.post(`${API}/purchases/final-budgets/from-ap/${id}`, {}, { withCredentials: true });
      toast.success("Presupuestos Finales generados exitosamente");
      setShowNew(false);
      setSelectedAPId("");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar"); }
    finally { setGenerating(false); }
  };

  const openEdit = (pf) => {
    setEditPF(pf);
    setEditData({ sucursal: pf.sucursal || "", condicion_pago: pf.condicion_pago || "", notes: pf.notes || "" });
    setProvSucursales([]);
    setProvCondiciones([]);
    // Fetch provider branches + condiciones de pago from catalog
    if (pf.provider_name) {
      axios.get(`${API}/listas/proveedores/search?q=${encodeURIComponent(pf.provider_name)}`, { withCredentials: true })
        .then(res => {
          const match = (res.data || []).find(p =>
            (p.proveedor || "").toLowerCase().trim() === (pf.provider_name || "").toLowerCase().trim()
          );
          if (match?.sucursales?.length > 0) setProvSucursales(match.sucursales);
          else if (match?.sucursal) setProvSucursales([match.sucursal]);
          if (Array.isArray(match?.condiciones_pago) && match.condiciones_pago.length > 0) {
            setProvCondiciones(match.condiciones_pago.filter(Boolean));
          }
        })
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!editPF) return;
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/final-budgets/${editPF._id}`, editData, { withCredentials: true });
      toast.success("Presupuesto Final actualizado");
      setEditPF(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const generatePFPDF = (pf) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pw = doc.internal.pageSize.getWidth();
    const CEN_BLUE = [46, 109, 164];
    doc.setFillColor(...CEN_BLUE);
    doc.rect(0, 0, pw, 28, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("CEN INGENIERIA E.A.S.", 14, 11);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Instalaciones Eléctricas — Asunción, Paraguay", 14, 17);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("PRESUPUESTO FINAL", pw - 14, 11, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(pf.number || "", pw - 14, 17, { align: "right" });
    doc.setTextColor(40, 40, 40);
    let y = 36;
    const info = [
      ["Proyecto / Obra:", pf.project_name || "—"],
      ["Proveedor:", pf.provider_name || "—"],
      ["Sucursal:", pf.sucursal || "—"],
      ["Condición de pago:", pf.condicion_pago || "—"],
      ["AP origen:", pf.ap_number || "—"],
      ["Creado por:", pf.created_by_name || "—"],
      ["Fecha:", formatDate(pf.created_at)],
    ];
    info.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(label, 14, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value), 64, y);
      y += 5.5;
    });
    autoTable(doc, {
      startY: y + 4,
      head: [["Descripción", "UM", "Cant.", "P.U. (Gs.)", "Subtotal (Gs.)"]],
      body: agruparPorMaterial(pf.items, true).map(item => [  // Fix EG (2026-09-25)
        item.item_name || "",
        item.unit || "",
        item.quantity || 0,
        formatPYG(item.unit_price || 0),
        formatPYG(item.subtotal || 0),
      ]),
      foot: [["", "", "", "TOTAL:", formatPYG(pf.total || 0)]],
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: CEN_BLUE, textColor: [255, 255, 255], fontStyle: "bold" },
      footStyles: { fillColor: [220, 235, 245], fontStyle: "bold", textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [245, 249, 255] },
      columnStyles: {
        0: { cellWidth: 65 },
        1: { cellWidth: 15, halign: "center" },
        2: { cellWidth: 15, halign: "center" },
        3: { cellWidth: 38, halign: "right" },
        4: { cellWidth: 38, halign: "right" },
      },
      margin: { left: 14, right: 14 }
    });
    if (pf.notes) {
      const finalY = doc.lastAutoTable.finalY + 6;
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CEN_BLUE);
      doc.text("Observaciones:", 14, finalY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      doc.text(pf.notes, 14, finalY + 5, { maxWidth: pw - 28 });
    }
    guardarPDF(doc, `PF_${pf.number}.pdf`);
  };

  const selectedAP = budgetActs.find(a => a._id === selectedAPId);

  const STATUS_PF = {
    borrador:     { label: "Borrador",     cls: "bg-gray-100 text-gray-600 border-gray-200" },
    oc_generada:  { label: "OC Generada",  cls: "bg-cyan-100 text-cyan-700 border-cyan-200" },
    facturada:    { label: "Facturada",    cls: "bg-blue-100 text-blue-700 border-blue-200" },
  };

  return (
    <div className="space-y-4">
      {/* Hidden file input for PF document upload */}
      <input
        type="file"
        ref={pfFileInputRef}
        className="hidden"
        accept="image/*,application/pdf"
        onChange={handlePFFileSelected}
      />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">{filteredPFs.length} de {finalBudgets.length} presupuestos finales</p>
        <div className="flex items-center gap-2 flex-wrap">
          <FiltroClienteObraPresupuesto
            listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
            filterCliente={filterCliente} setFilterCliente={setFilterCliente}
            filterObra={filterObra} setFilterObra={setFilterObra}
            filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
            testIdPrefix="ptos-finales"
          />
          <BusquedaEstadoUrgenciaFilters
            search={search} setSearch={setSearch}
            searchPlaceholder="Buscar por N°, AP, obra o proveedor..."
            filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            statusOptions={[
              { value: "todos", label: `Todos (${baseFilteredPFs.length})` },
              { value: "borrador", label: `Borrador (${pfStatusCounts.borrador || 0})` },
              { value: "oc_generada", label: `OC Generada (${pfStatusCounts.oc_generada || 0})` },
              { value: "facturada", label: `Facturada (${pfStatusCounts.facturada || 0})` },
            ]}
            filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
            testIdPrefix="ptos-finales"
          />
          {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
            <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
              data-testid="ptos-finales-clear-filter">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* PENDIENTES: APs cerrados sin PF — lista inline */}
      {canEdit && closedAPsForPF.length > 0 && (
        <div className="bg-cyan-50 rounded-xl border border-cyan-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-cyan-100/70 border-b border-cyan-200 flex items-center gap-2">
            <FileCheck size={13} className="text-cyan-600" />
            <p className="text-xs font-bold text-cyan-800">Análisis cerrados pendientes de PF — {closedAPsForPF.length}</p>
          </div>
          <div className="divide-y divide-cyan-100/60">
            {closedAPsForPF.map(act => (
              <div key={act._id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-cyan-50/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-bold text-gray-900 font-mono">{act.number}</span>
                  <span className="text-xs text-gray-500 ml-2">{act.project_name} · {(act.provider_columns || []).length} proveedores</span>
                </div>
                <button
                  onClick={() => handleGenerate(act._id)}
                  disabled={generating}
                  className="flex items-center gap-1 text-xs bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-semibold flex-shrink-0 transition-all"
                  data-testid={`generar-pf-inline-${act._id}`}>
                  {generating ? <Loader2 size={11} className="animate-spin" /> : <FileCheck size={11} />}
                  Generar PF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-cyan-50/70 rounded-xl border border-cyan-100 p-4 text-xs text-cyan-800">
        <p className="font-semibold mb-1">Presupuesto Final (PF)</p>
        <p>Se genera desde un AP cerrado, un PF por proveedor ganador. Cada PF contiene los ítems adjudicados a ese proveedor con sucursal de retiro y condición de pago. Es el documento formal previo a la Orden de Compra (OC).</p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div>
      ) : filteredPFs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <FileCheck size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay presupuestos finales</p>
          <p className="text-xs text-gray-400 mt-1">
            {closedAPsForPF.length > 0
              ? "Use el botón «Generar PF» en la sección superior para crear desde un AP cerrado"
              : "Primero cierre un Análisis de Presupuesto (AP)"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPFs.map(pf => {
            const statusInfo = STATUS_PF[pf.status] || STATUS_PF.borrador;
            return (
              <div key={pf._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm hover:shadow-md transition-all" data-testid="pf-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-900 text-sm font-mono">{pf.number}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusInfo.cls}`}>{statusInfo.label}</span>
                    </div>
                    <p className="text-xs text-gray-700 font-semibold mt-1">{pf.provider_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{pf.project_name} · AP: {pf.ap_number}</p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {pf.sucursal && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          <MapPin size={10} />{pf.sucursal}
                        </span>
                      )}
                      {pf.condicion_pago && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          <Clock size={10} />{pf.condicion_pago}
                        </span>
                      )}
                      {pf.total > 0 && (
                        <span className="text-xs font-bold text-[#2E6DA4]">{formatPYG(pf.total)}</span>
                      )}
                      <span className="text-[10px] text-gray-400">{agruparPorMaterial(pf.items, true).length} ítems · {formatDate(pf.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {canEdit && (
                      <button onClick={() => openEdit(pf)} title="Editar sucursal / condición de pago"
                        className="p-1.5 rounded-lg hover:bg-cyan-50 text-cyan-600 transition-colors"
                        data-testid="edit-pf-btn">
                        <Pencil size={14} />
                      </button>
                    )}
                    {pf.approved_doc_name ? (
                      <a href={`${API}/purchases/final-budgets/${pf._id}/approved-doc?inline=1`} target="_blank" rel="noreferrer" title={pf.approved_doc_name}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors"
                        data-testid={`doc-pf-${pf._id}`}>
                        <Paperclip size={14} />
                      </a>
                    ) : canEdit && (
                      <button onClick={() => handleUploadPFDoc(pf._id)} title="Adjuntar presupuesto final aprobado"
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-300 hover:text-gray-500 transition-colors"
                        data-testid={`upload-pf-${pf._id}`}>
                        <UploadCloud size={14} />
                      </button>
                    )}
                    <button onClick={() => setViewPF(pf)} title="Ver Presupuesto Final"
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-[#2E6DA4] transition-colors"
                      data-testid="ver-pf-btn">
                      <Eye size={15} />
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => { setAiPF(pf); setAiFile(null); setAiResult(pf.ai_analysis || null); }}
                        title="Validar Presupuesto Final con IA"
                        className="p-1.5 rounded-lg hover:bg-violet-50 text-violet-600 transition-colors"
                        data-testid={`ai-analyze-pf-${pf._id}`}
                      >
                        <Brain size={15} />
                      </button>
                    )}
                    <button onClick={() => generatePFPDF(pf)} title="Descargar PDF"
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="pdf-pf-btn">
                      <Download size={15} />
                    </button>
                    {canDelete && pf.ap_id && (
                      <button onClick={() => handleRevertToAnalysis(pf)} disabled={revertingId === pf._id}
                        title="Revertir a Análisis (volver a seleccionar proveedores)"
                        className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50"
                        data-testid={`revert-pf-btn-${pf._id}`}>
                        {revertingId === pf._id ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => setDeleteTarget(pf)} title="Eliminar PF"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                        data-testid={`delete-pf-btn-${pf._id}`}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: Editar PF */}
      {editPF && (
        <Modal open={!!editPF} onClose={() => setEditPF(null)} title={`Editar PF: ${editPF.number}`}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proveedor</label>
              <input value={editPF.provider_name} disabled
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 text-gray-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sucursal de retiro</label>
              {provSucursales.length > 1 ? (
                <select
                  value={editData.sucursal}
                  onChange={e => setEditData(prev => ({ ...prev, sucursal: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                  data-testid="pf-sucursal-select">
                  <option value="">Seleccionar sucursal...</option>
                  {provSucursales.map((s, i) => <option key={i} value={s}>{s}</option>)}
                  <option value="__manual__">Ingresar manualmente...</option>
                </select>
              ) : (
                <input
                  value={editData.sucursal}
                  onChange={e => setEditData(prev => ({ ...prev, sucursal: e.target.value }))}
                  placeholder={provSucursales.length === 1 ? provSucursales[0] : "Ej: Sucursal Asunción Centro"}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                  data-testid="pf-sucursal-input"
                />
              )}
              {provSucursales.length === 1 && (
                <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                  <MapPin size={10} /> Sucursal del catálogo: <strong>{provSucursales[0]}</strong>
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Condición de pago</label>
              {provCondiciones.length > 0 ? (
                <>
                  <select
                    value={editData.condicion_pago}
                    onChange={e => setEditData(prev => ({ ...prev, condicion_pago: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                    data-testid="pf-condicion-input">
                    <option value="">Seleccionar condición del proveedor...</option>
                    {provCondiciones.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    {editData.condicion_pago && !provCondiciones.includes(editData.condicion_pago) && (
                      <option value={editData.condicion_pago}>{editData.condicion_pago} (actual)</option>
                    )}
                  </select>
                  <p className="text-[10px] text-gray-400 mt-1">Desplegado desde el catálogo del proveedor.</p>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={editData.condicion_pago}
                    onChange={e => setEditData(prev => ({ ...prev, condicion_pago: e.target.value }))}
                    placeholder="Ej: Contado, 30 días, 30/60/90 días"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
                    data-testid="pf-condicion-input"
                  />
                  <p className="text-[10px] text-amber-600 mt-1">Sin condiciones registradas para este proveedor — agrégalas en Listas → Proveedores.</p>
                </>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
              <textarea
                value={editData.notes}
                onChange={e => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Ej: Retiro acordado lunes y martes de 8 a 12 hs."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none resize-none"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditPF(null)}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                data-testid="save-pf-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: VER PF */}
      {viewPF && (
        <Modal open={!!viewPF} onClose={() => setViewPF(null)} title={`PF: ${viewPF.number}`} extraWide>
          <div className="space-y-5">
            <div className="bg-[#2E6DA4] rounded-xl p-5 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium opacity-70 uppercase tracking-wider">CEN INGENIERIA E.A.S.</p>
                  <p className="text-xs opacity-60 mt-0.5">Instalaciones Eléctricas — Asunción, Paraguay</p>
                </div>
                <div className="text-right">
                  <p className="text-xs opacity-70 uppercase">Presupuesto Final</p>
                  <p className="font-mono font-bold text-lg mt-0.5">{viewPF.number}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: "Proyecto / Obra", value: viewPF.project_name },
                { label: "Proveedor",       value: viewPF.provider_name },
                { label: "Sucursal",        value: viewPF.sucursal || "—" },
                { label: "Cond. de pago",   value: viewPF.condicion_pago || "—" },
                { label: "AP Origen",       value: viewPF.ap_number },
                { label: "Fecha Objetivo",  value: viewPF.target_date ? formatDate(viewPF.target_date) : "—" },
                { label: "Total",           value: formatPYG(viewPF.total || 0) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1 break-words">{value || "—"}</p>
                </div>
              ))}
            </div>

            {/* Fix O (2026-08-31): historial del pedido de origen en cada paso del flujo. */}
            <HistorialPedidoCard pmId={viewPF.pm_origen_id} />

            <div>
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Ítems Adjudicados</h4>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#2E6DA4] text-white">
                      {["Descripción", "UM", "Cant.", "P.U. (Gs.)", "Subtotal (Gs.)"].map(h => (
                        <th key={h} className="text-left px-3 py-2.5 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agruparPorMaterial(viewPF.items, true).map((item, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-cyan-50/40"}>
                        <td className="px-3 py-2.5 font-medium text-gray-800">
                          {item.item_name}
                          {item.__lineas > 1 && <span className="block text-[10px] font-normal text-gray-400" data-testid={`pf-item-lineas-${i}`}>Total de {item.__lineas} ítems del presupuesto</span>}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 text-center">{item.unit}</td>
                        <td className="px-3 py-2.5 font-bold text-gray-900 text-center">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-right text-gray-700">{formatPYG(item.unit_price || 0)}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-[#2E6DA4]">{formatPYG(item.subtotal || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      <td colSpan={4} className="px-3 py-2.5 text-right text-gray-700 text-xs">TOTAL:</td>
                      <td className="px-3 py-2.5 text-right text-[#2E6DA4] font-mono text-sm">{formatPYG(viewPF.total || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {viewPF.notes && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Observaciones</p>
                <p className="text-sm text-blue-800 mt-1">{viewPF.notes}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => generatePFPDF(viewPF)}
                className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                data-testid="pdf-pf-modal-btn">
                <Download size={14} /> Descargar PDF
              </button>
              <button onClick={() => setViewPF(null)}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Analizar PFM con IA — PDF REV COMPRAS MAT */}
      {aiPF && (
        <Modal open={!!aiPF} onClose={() => { setAiPF(null); setAiFile(null); setAiResult(null); }}
          title={`Validación IA · PFM ${aiPF.number}`}>
          <div className="space-y-4">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-violet-800">
              <p className="font-semibold mb-1 flex items-center gap-1.5"><Sparkles size={12} /> Análisis con IA del Presupuesto Final</p>
              <p>Adjuntá el documento del proveedor (PDF/imagen). La IA comparará ítems, precios y total contra los esperados del PFM <strong>{aiPF.number}</strong> (Total: <strong>{formatPYG(aiPF.total || 0)}</strong>).</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Documento del proveedor (PDF / imagen, máx 5MB)</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={e => setAiFile(e.target.files?.[0] || null)}
                className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white file:font-semibold file:text-xs hover:file:bg-violet-700 file:cursor-pointer"
                data-testid="ai-pf-file-input"
              />
            </div>

            {aiResult && (
              <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Resultado IA</p>
                    <p className="text-sm font-semibold text-gray-800">Proveedor detectado: {aiResult.proveedor_detectado || "—"}</p>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-1 rounded-full uppercase ${
                    aiResult.recomendacion === "aprobar" ? "bg-emerald-100 text-emerald-700 border border-emerald-200" :
                    aiResult.recomendacion === "rechazar" ? "bg-red-100 text-red-700 border border-red-200" :
                    "bg-amber-100 text-amber-700 border border-amber-200"
                  }`}>{aiResult.recomendacion || "revisar"}</span>
                </div>
                <div className="text-xs text-gray-700">
                  Total doc: <strong>{aiResult.total_doc ? formatPYG(aiResult.total_doc) : "—"}</strong> · Match: <strong className="uppercase">{aiResult.match_total || "—"}</strong>
                </div>
                {(aiResult.alertas || []).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">Alertas</p>
                    <ul className="text-xs text-amber-800 list-disc list-inside space-y-0.5">
                      {aiResult.alertas.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}
                {(aiResult.items_validados || []).length > 0 && (
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-400 uppercase">
                          <th className="px-2 py-1.5">Ítem</th>
                          <th className="px-2 py-1.5 text-right">Esperado</th>
                          <th className="px-2 py-1.5 text-right">Encontrado</th>
                          <th className="px-2 py-1.5 text-right">Dif %</th>
                          <th className="px-2 py-1.5 text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiResult.items_validados.map((it, i) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="px-2 py-1.5 text-gray-800">{it.item_name}</td>
                            <td className="px-2 py-1.5 text-right text-gray-700">{it.esperado ? formatPYG(it.esperado) : "—"}</td>
                            <td className="px-2 py-1.5 text-right text-gray-700">{it.encontrado ? formatPYG(it.encontrado) : "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{it.diferencia_pct != null ? `${it.diferencia_pct.toFixed(1)}%` : "—"}</td>
                            <td className={`px-2 py-1.5 text-center font-bold uppercase text-[10px] ${
                              it.estado === "ok" ? "text-emerald-600" :
                              it.estado === "alerta" ? "text-amber-600" :
                              "text-red-600"
                            }`}>{it.estado}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setAiPF(null); setAiFile(null); setAiResult(null); }}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">
                Cerrar
              </button>
              <button onClick={handleAIAnalyze} disabled={aiLoading || !aiFile}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-2.5 text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="ai-pf-analyze-btn">
                {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
                {aiLoading ? "Analizando..." : (aiResult ? "Re-analizar" : "Analizar")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal confirmar eliminación de PF (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Presupuesto Final">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el PF <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeletePF} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-pf-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

